import React, { useState, useEffect, useCallback, useRef } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment-timezone";
import "moment/locale/en-gb";
import { StyledDateTextField } from "./style";
import { getMonthFromHeader, getWeekBounds, isWeekSelectableInRange } from "../../../api_calls/utils";

const PRIMARY_COLOR = "#AF3241";
const PRIMARY_COLOR_DARK = "#8a2833";

const TABBABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

/** Move focus to the next/previous tabbable outside pickers/modals that steal it. */
const focusAdjacentTabbable = (from, shiftKey) => {
  if (!from || typeof document === "undefined") return;
  const candidates = Array.from(document.querySelectorAll(TABBABLE_SELECTOR)).filter((el) => {
    if (!(el instanceof HTMLElement)) return false;
    if (el.tabIndex < 0) return false;
    // Skip the open picker list itself; do NOT skip .MuiModal-root — these
    // fields live inside dialogs and every in-dialog tab target would vanish.
    if (el.closest(".MuiPickersPopper-root")) return false;
    if (el.closest("[aria-hidden='true']")) return false;
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  });
  const index = candidates.indexOf(from);
  if (index === -1) return;
  const target = candidates[shiftKey ? index - 1 : index + 1];
  target?.focus();
};

/**
 * Returns a user-facing validation message explaining why the date is invalid.
 * @param {moment.Moment} date - The date to validate
 * @param {moment.Moment|null} minMoment - Minimum allowed date
 * @param {moment.Moment|null} maxMoment - Maximum allowed date
 * @param {Function|undefined} enabledDates - Optional predicate for selectable dates
 * @param {string} prefix - Field name prefix for error messages
 * @returns {string} Validation message or empty string if valid
 */
const getValidationMessage = (date, minMoment, maxMoment, enabledDates, prefix) => {
  if (!date || !moment.isMoment(date) || !date.isValid()) {
    return "Please enter a valid date (DD/MM/YYYY)";
  }
  if (minMoment && date.isBefore(minMoment, "day")) {
    return minMoment.isSame(moment(), "day")
      ? prefix + " date cannot be in the past"
      : prefix + ` date cannot be before ${minMoment.format("DD/MM/YYYY")}`;
  }
  if (maxMoment && date.isAfter(maxMoment, "day")) {
    return maxMoment.isSame(moment(), "day")
      ? prefix + " date cannot be in the future"
      : prefix + ` date cannot be after ${maxMoment.format("DD/MM/YYYY")}`;
  }
  if (enabledDates) {
    const dateObj = new Date(date.year(), date.month(), date.date());
    if (!enabledDates(dateObj)) {
      return "This date is not available";
    }
  }
  return "";
};

/**
 * Derives field role from label for week selection callbacks
 * @param {string} label - The field label
 * @returns {'start' | 'end' | null} The derived field role
 */
const deriveFieldRole = (label) => {
  if (!label) return null;
  const lowerLabel = label.toLowerCase();
  if (lowerLabel.includes("start")) return "start";
  if (lowerLabel.includes("end")) return "end";
  return null;
};

/**
 * Replaces the picker's action bar with legend rows ("[color box] = label"),
 * one per highlight entry — the action buttons (Clear) are intentionally
 * dropped in this variant. The layout grid places the action bar via the
 * MuiPickersLayout-actionBar class, so the root Box must carry the incoming
 * className to keep its grid position.
 */
const HighlightLegendActionBar = ({ legendItems, className }) => {
  if (!legendItems || legendItems.length === 0) return null;
  return (
    <Box className={className} sx={{ px: 1.5, pt: 0, pb: 1 }}>
      {legendItems.map(({ key, color, borderColor, label }) => (
        <Stack key={key} direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.25 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: "2px",
              backgroundColor: color,
              border: `1px solid ${borderColor || color}`,
              flexShrink: 0,
            }}
          />
          <Typography variant="caption" sx={{ color: "#555", lineHeight: 1.4 }}>
            = {label}
          </Typography>
        </Stack>
      ))}
    </Box>
  );
};

/**
 * Generates slot props for MUI DatePicker with custom styling
 */
const getSlotProps = (onOpenPicker, isPickerOpen, onBlurHandler, inputRef, fieldError, showSelection, onKeyDownHandler, reserveErrorSpace, highlightedDates, highlightLegend) => {
  const daySx = {
    width: 28,
    height: 28,
    fontSize: "0.75rem",
    "&:focus:not(.Mui-selected)": {
      backgroundColor: "transparent",
      outline: "none",
      "&:hover": {
        backgroundColor: "rgba(138, 40, 51, 0.2)",
      },
    },
    "&.Mui-selected": {
      backgroundColor: PRIMARY_COLOR,
      color: "#fff",
      "&:hover": { backgroundColor: PRIMARY_COLOR_DARK },
      "&:focus": { backgroundColor: PRIMARY_COLOR },
      "&.Mui-focusVisible": {
        backgroundColor: PRIMARY_COLOR,
        outline: `2px solid ${PRIMARY_COLOR_DARK}`,
        outlineOffset: -2,
      },
    },
    "&:hover": { backgroundColor: "rgba(138, 40, 51, 0.2)" },
    "&.Mui-focusVisible:not(.Mui-selected)": {
      outline: `2px solid ${PRIMARY_COLOR}`,
      outlineOffset: -2,
      backgroundColor: "rgba(175, 50, 65, 0.08)",
    },
    "&.MuiPickersDay-today": {
      border: "none",
      "&:not(.Mui-selected)": { border: "none" },
    },
    "&.MuiPickersDay-dayOutsideMonth:not(.Mui-disabled)": { color: "#777" },
    "&.Mui-disabled:not(.MuiPickersDay-dayOutsideMonth)": {
      color: "#ccc",
      opacity: 1,
      "&:hover": { cursor: "not-allowed" },
    },
    "&.Mui-disabled.MuiPickersDay-dayOutsideMonth": {
      color: "#e0e0e0",
      opacity: 0.5,
      cursor: "not-allowed",
    },
  };

  // One sx per legend entry, keyed by the value the highlightedDates predicate
  // returns. Highlighted days keep their background while focused (the base sx
  // resets it to transparent); selected/hover states still win via their own
  // selectors.
  const highlightSxByKey = {};
  if (highlightedDates && Array.isArray(highlightLegend)) {
    highlightLegend.forEach(({ key, color }) => {
      highlightSxByKey[key] = {
        ...daySx,
        backgroundColor: color,
        "&:focus:not(.Mui-selected)": {
          ...daySx["&:focus:not(.Mui-selected)"],
          backgroundColor: color,
        },
      };
    });
  }

  return {
    textField: {
      variant: "standard",
      fullWidth: true,
      autoComplete: "off",
      onClick: onOpenPicker,
      onBlur: onBlurHandler,
      onKeyDown: onKeyDownHandler,
      inputRef: inputRef,
      error: fieldError !== "" && fieldError != null,
      // Blank space (not "") keeps the helper mounted so the reserved height holds.
      helperText:
        fieldError !== "" && fieldError != null
          ? fieldError
          : reserveErrorSpace
            ? " "
            : "",
      FormHelperTextProps: {
        sx: {
          color: "#d32f2f",
          marginLeft: 0,
          fontSize: "0.75rem",
          ...(reserveErrorSpace
            ? {
              // One line stays reserved via the mounted " " helper; longer
              // errors wrap and grow the row instead of padding it upfront.
              marginTop: 0,
              lineHeight: 1.3,
              whiteSpace: "normal",
              overflowWrap: "break-word",
            }
            : {}),
        },
      },
      InputProps: {
        readOnly: !isPickerOpen,
        sx: {
          cursor: isPickerOpen ? "text" : "pointer",
          caretColor: showSelection ? PRIMARY_COLOR : "transparent",
          "&:before": {
            borderBottomColor: "rgba(0, 0, 0, 0.42)",
          },
          "&:hover:not(.Mui-disabled):before": {
            borderBottomColor: "rgba(0, 0, 0, 0.87)",
          },
          "&:after": {
            borderBottomColor: PRIMARY_COLOR,
          },
          "& input": {
            cursor: isPickerOpen ? "text" : "pointer",
            caretColor: showSelection ? PRIMARY_COLOR : "transparent",
            "&::selection": {
              backgroundColor: showSelection ? "rgba(175, 50, 65, 0.3)" : "transparent",
            },
          },
        },
      },
      InputLabelProps: {
        sx: {
          "&.Mui-focused": {
            color: PRIMARY_COLOR,
          },
        },
      },
    },
    // With a highlight predicate the day slot props become per-day: the callback
    // form receives the rendered day in ownerState, so covered days can get the
    // highlight background of their legend entry while all others keep the plain sx.
    day: !highlightedDates
      ? { sx: daySx }
      : (ownerState) => {
        const day = ownerState.day;
        const key =
          day && highlightedDates(new Date(day.year(), day.month(), day.date()));
        return { sx: (key && highlightSxByKey[key]) || daySx };
      },
    calendarHeader: {
      sx: {
        paddingLeft: 1,
        paddingRight: 1,
        marginTop: 0,
        marginBottom: 0,
        minHeight: 36,
        "& .MuiPickersCalendarHeader-label": { fontSize: "0.875rem" },
        "& .MuiPickersCalendarHeader-switchViewButton": {
          color: PRIMARY_COLOR,
          padding: 0.5,
          "&:focus": { outline: `2px solid ${PRIMARY_COLOR}`, outlineOffset: -2 },
        },
        "& .MuiPickersArrowSwitcher-button": {
          color: PRIMARY_COLOR,
          padding: 0.5,
          "&:focus": { outline: `2px solid ${PRIMARY_COLOR}`, outlineOffset: -2 },
        },
      },
    },
    yearButton: {
      sx: {
        "&.Mui-selected": {
          backgroundColor: PRIMARY_COLOR,
          color: "#fff",
          "&:hover": { backgroundColor: PRIMARY_COLOR_DARK },
          "&:focus": { backgroundColor: PRIMARY_COLOR },
          "&.Mui-focusVisible": {
            backgroundColor: PRIMARY_COLOR,
            outline: `2px solid ${PRIMARY_COLOR_DARK}`,
            outlineOffset: -2,
          },
        },
        "&.Mui-focusVisible:not(.Mui-selected)": {
          outline: `2px solid ${PRIMARY_COLOR}`,
          outlineOffset: -2,
          backgroundColor: "rgba(175, 50, 65, 0.08)",
        },
      },
    },
    monthButton: {
      sx: {
        "&.Mui-selected": {
          backgroundColor: PRIMARY_COLOR,
          color: "#fff",
          "&:hover": { backgroundColor: PRIMARY_COLOR_DARK },
          "&:focus": { backgroundColor: PRIMARY_COLOR },
          "&.Mui-focusVisible": {
            backgroundColor: PRIMARY_COLOR,
            outline: `2px solid ${PRIMARY_COLOR_DARK}`,
            outlineOffset: -2,
          },
        },
        "&.Mui-focusVisible:not(.Mui-selected)": {
          outline: `2px solid ${PRIMARY_COLOR}`,
          outlineOffset: -2,
          backgroundColor: "rgba(175, 50, 65, 0.08)",
        },
      },
    },
    layout: {
      sx: {
        "& .MuiDateCalendar-root": { width: 260, height: "auto" },
        "& .MuiDayCalendar-header .MuiDayCalendar-weekDayLabel": {
          width: 28, height: 28, fontSize: "0.7rem", color: "#555",
        },
        "& .MuiDayCalendar-weekNumberLabel": {
          width: 28, fontSize: "0.7rem", color: "#555", fontWeight: 500, backgroundColor: "#f5f5f5",
        },
        "& .MuiDayCalendar-weekNumber": {
          width: 28, height: 28, fontSize: "0.7rem", cursor: "pointer",
          backgroundColor: "#f5f5f5", color: "#555",
          transition: "color 0.15s ease, background-color 0.15s ease",
          "&:hover": { color: PRIMARY_COLOR_DARK, fontWeight: 600, backgroundColor: "#e0e0e0" },
        },
        "& .MuiDayCalendar-weekNumber.week-number-disabled": {
          color: "#ccc", cursor: "not-allowed",
          "&:hover": { color: "#ccc", fontWeight: "normal" },
        },
        "& .MuiDayCalendar-slideTransition": { minHeight: 182, overflow: "hidden" },
        "& .MuiPickersDay-root": { width: 28, height: 28, color: "#000" },
        "& .MuiYearCalendar-root": {
          width: 260, maxHeight: 220, padding: "4px 0",
          "& .MuiPickersYear-yearButton": {
            width: 56, height: 28, fontSize: "0.8rem", margin: "2px 4px",
            "&.Mui-selected": {
              backgroundColor: PRIMARY_COLOR, color: "#fff",
              "&:hover": { backgroundColor: PRIMARY_COLOR_DARK },
            },
            "&.Mui-focusVisible:not(.Mui-selected)": {
              outline: `2px solid ${PRIMARY_COLOR}`, outlineOffset: -2,
              backgroundColor: "rgba(175, 50, 65, 0.08)",
            },
            "&.Mui-selected.Mui-focusVisible": {
              backgroundColor: PRIMARY_COLOR,
              outline: `2px solid ${PRIMARY_COLOR_DARK}`, outlineOffset: -2,
            },
          },
        },
        "& .MuiMonthCalendar-root": {
          width: 260, padding: "4px 0",
          "& .MuiPickersMonth-monthButton": {
            width: 56, height: 28, fontSize: "0.8rem", margin: "2px 4px",
            "&.Mui-selected": {
              backgroundColor: PRIMARY_COLOR, color: "#fff",
              "&:hover": { backgroundColor: PRIMARY_COLOR_DARK },
            },
            "&.Mui-disabled": { opacity: 0.4, cursor: "not-allowed" },
            "&.Mui-focusVisible:not(.Mui-selected)": {
              outline: `2px solid ${PRIMARY_COLOR}`, outlineOffset: -2,
              backgroundColor: "rgba(175, 50, 65, 0.08)",
            },
            "&.Mui-selected.Mui-focusVisible": {
              backgroundColor: PRIMARY_COLOR,
              outline: `2px solid ${PRIMARY_COLOR_DARK}`, outlineOffset: -2,
            },
          },
        },
      },
    },
    popper: {
      placement: "bottom-start",
      disableAutoFocus: true,
      // Without this, MUI restores focus to the field on close and fights Tab.
      disableRestoreFocus: true,
      modifiers: [{ name: "preventOverflow", options: { padding: 8 } }],
      sx: {
        zIndex: 1300,
        "& .MuiPaper-root": { boxShadow: "0 4px 20px rgba(0,0,0,0.15)" },
      },
    },
    // Explicit Clear action: the field is read-only until opened, so this is the
    // reliable way to empty a date. Emits onChange(null), which resets the value.
    // legendItems is only set together with the HighlightLegendActionBar slot,
    // which consumes it (and drops the Clear button) — the default action bar
    // would leak it onto the DOM.
    actionBar: {
      actions: ["clear"],
      ...(highlightLegend ? { legendItems: highlightLegend } : {}),
      sx: {
        "& .MuiButton-root": {
          color: PRIMARY_COLOR,
          fontWeight: 600,
          fontSize: "0.8rem",
        },
      },
    },
  };
};

export default function DateInputField({
  label,
  elemId,
  fieldName,
  defaultVal,
  fieldRequired,
  onChangeCall,
  fieldError,
  minSelectable,
  maxSelectable,
  enabledDates,
  disabled,
  className,
  setDialogExpanded,
  onWeekSelect,
  weekSelectionMinDate,
  weekSelectionMaxDate,
  showDaysOutsideCurrentMonth,
  reserveErrorSpace = false,
  // Keep an untouched parent value even if it sits outside the current min/max.
  preserveStoredValue = false,
  // Predicate (Date => key | null); days whose key matches a highlightLegend
  // entry get that entry's background color.
  highlightedDates,
  // Array of { key, color, borderColor?, label } rendered as a legend at the
  // bottom of the picker; replaces the action bar (no Clear button) when set.
  highlightLegend,
}) {
  const [selectedDate, setSelectedDate] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [pickerView, setPickerView] = useState("day");
  const [showSelection, setShowSelection] = useState(false);
  const [validationError, setValidationError] = useState("");
  const inputRef = useRef(null);
  const isClosingRef = useRef(false);
  // True while Tab is closing the picker — blocks blur/onClose from yanking focus
  // back onto this field after we have already moved it forward.
  const tabClosingRef = useRef(false);
  const errorSetAtRef = useRef(null);
  const ERROR_IMMUNITY_MS = 2000;
  // Used with preserveStoredValue to skip validating untouched parent values.
  const isUserEditedRef = useRef(false);

  const enableWeekSelection = typeof onWeekSelect === "function";
  const fieldRole = deriveFieldRole(label);

  const effectiveMaxDate = maxSelectable
    ? moment(maxSelectable)
    : moment().add(2, "years").endOf("year");

  const minMoment = minSelectable ? moment(minSelectable) : null;

  const isDateValid = useCallback((date) => {
    if (!date || !moment.isMoment(date) || !date.isValid()) return false;
    if (minMoment && date.isBefore(minMoment, "day")) return false;
    if (effectiveMaxDate && date.isAfter(effectiveMaxDate, "day")) return false;
    if (enabledDates) {
      const dateObj = new Date(date.year(), date.month(), date.date());
      if (!enabledDates(dateObj)) return false;
    }
    return true;
  }, [minSelectable, effectiveMaxDate, enabledDates]);

  /**
  * Validates the current input. Clears the field on invalid input
  * and shows a descriptive error message.
  */
  const validateInput = useCallback(() => {
    if (disabled) {
      setValidationError("");
      return;
    }
    if (!selectedDate) {
      // Respect error immunity period before clearing validation errors
      const errorAge = errorSetAtRef.current !== null
        ? Date.now() - errorSetAtRef.current
        : Infinity;
      if (errorAge > ERROR_IMMUNITY_MS) {
        setValidationError("");
      }
      return;
    }
    if (preserveStoredValue && !isUserEditedRef.current) return;
    const message = getValidationMessage(selectedDate, minMoment, effectiveMaxDate, enabledDates, label.split(" ")[0]);
    if (message) {
      setValidationError(message);
      errorSetAtRef.current = Date.now();
      setSelectedDate(null);
      onChangeCall(null);
    } else {
      setValidationError("");
    }
  }, [disabled, selectedDate, minMoment, effectiveMaxDate, enabledDates, onChangeCall, label, preserveStoredValue]);

  /**
   * Closes the picker and validates input. When `blurInput` is true (Enter /
   * outside click), dual-timeout blur covers MUI's async focus restoration.
   * Tab passes false and sets tabClosingRef so onClose cannot escalate to blur
   * and pull focus back onto this field.
   */
  const closePicker = useCallback((blurInput = true) => {
    isClosingRef.current = true;
    validateInput();
    setIsOpen(false);
    setPickerView("day");
    window.getSelection()?.removeAllRanges();
    if (setDialogExpanded) {
      setDialogExpanded(false);
    }
    const shouldBlur = blurInput && !tabClosingRef.current;
    if (shouldBlur) {
      // Primary blur attempt after MUI's typical focus restoration
      setTimeout(() => {
        inputRef.current?.blur();
      }, 100);
      // Secondary fallback for edge cases where validation triggers delayed focus
      setTimeout(() => {
        inputRef.current?.blur();
        isClosingRef.current = false;
      }, 300);
    } else {
      setTimeout(() => {
        isClosingRef.current = false;
        tabClosingRef.current = false;
      }, 300);
    }
  }, [validateInput, setDialogExpanded]);

  /**
   * Handles input blur - validates the input.
   * Skipped when closePicker already performed validation.
   */
  const handleInputBlur = useCallback(() => {
    if (isClosingRef.current) return;
    validateInput();
  }, [validateInput]);

  const handleDateChange = (newValue) => {
    isUserEditedRef.current = true;
    // Clearing the field yields null — propagate it so the parent (and any
    // derived overview) drops the previously selected date.
    if (newValue === null) {
      setSelectedDate(null);
      setValidationError("");
      onChangeCall(null);
      return;
    }
    if (!newValue.isValid()) return;
    const dateObj = new Date(newValue.year(), newValue.month(), newValue.date(), 0, 0, 0);
    setSelectedDate(newValue);
    if (isDateValid(newValue)) {
      setValidationError("");
    }
    onChangeCall(dateObj);
    if (setDialogExpanded) {
      setDialogExpanded(true);
    }
  };

  const handleOpen = useCallback(() => {
    if (disabled) return;
    const errorAge = errorSetAtRef.current !== null
      ? Date.now() - errorSetAtRef.current
      : Infinity;
    if (errorAge > ERROR_IMMUNITY_MS) {
      setValidationError("");
    }
    setIsOpen(true);
    if (setDialogExpanded) {
      setDialogExpanded(true);
    }
  }, [disabled, setDialogExpanded]);

  const preventScrollOnViewChange = useCallback(() => {
    const currentScrollPos = window.scrollY;
    requestAnimationFrame(() => {
      window.scrollTo(0, currentScrollPos);
    });
  }, []);

  useEffect(() => {
    if (defaultVal) {
      const newDate = moment(defaultVal);
      setSelectedDate(newDate);
      setValidationError("");
    } else {
      setSelectedDate(null);
      setValidationError("");
    }
  }, [defaultVal]);

  // A locked/empty field (e.g. End after "No End") must not keep a leftover error.
  useEffect(() => {
    if (disabled) setValidationError("");
  }, [disabled]);

  /**
   * Enter closes like an outside click (and blurs). Tab takes over focus
   * movement so MUI's restore-focus cannot snap back to this field.
   */
  const handleKeyDown = useCallback((e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      if (isOpen) {
        closePicker(true);
      }
      return;
    }
    if (e.key === "Tab" && isOpen) {
      e.preventDefault();
      e.stopPropagation();
      tabClosingRef.current = true;
      const from = inputRef.current;
      const shiftKey = e.shiftKey;
      closePicker(false);
      focusAdjacentTabbable(from, shiftKey);
      const ensureMoved = () => {
        if (from && (document.activeElement === from || from.contains?.(document.activeElement))) {
          focusAdjacentTabbable(from, shiftKey);
        }
      };
      setTimeout(ensureMoved, 0);
      setTimeout(ensureMoved, 50);
      setTimeout(ensureMoved, 150);
    }
  }, [isOpen, closePicker]);

  useEffect(() => {
    setShowSelection(isOpen);
  }, [isOpen]);

  /**
   * One-shot focus reclaim after MUI's popper steals focus on open.
   * Guarded by isClosingRef so it never fires during close.
   */
  useEffect(() => {
    if (!isOpen) return;
    const timerId = setTimeout(() => {
      if (isClosingRef.current) return;
      inputRef.current?.focus();
    }, 50);
    return () => clearTimeout(timerId);
  }, [isOpen]);

  const updateWeekNumberStyling = useCallback(() => {
    if (!enableWeekSelection) return;
    const weekNumbers = document.querySelectorAll(".MuiDayCalendar-weekNumber");
    weekNumbers.forEach((weekEl) => {
      const weekText = weekEl.textContent;
      if (!weekText) return;
      const weekNumber = parseInt(weekText, 10);
      if (isNaN(weekNumber)) return;
      const popper = weekEl.closest(".MuiPickersPopper-root");
      if (!popper) return;
      const monthHeader = popper.querySelector(".MuiPickersCalendarHeader-label");
      if (!monthHeader) return;
      const headerText = monthHeader.textContent || "";
      const yearMatch = headerText.match(/\d{4}/);
      const displayedYear = yearMatch ? parseInt(yearMatch[0], 10) : new Date().getFullYear();
      const displayedMonth = getMonthFromHeader(headerText);
      const isSelectable = isWeekSelectableInRange(
        weekNumber,
        displayedYear,
        displayedMonth,
        weekSelectionMinDate,
        weekSelectionMaxDate,
        fieldRole
      );
      if (isSelectable) {
        weekEl.classList.remove("week-number-disabled");
      } else {
        weekEl.classList.add("week-number-disabled");
      }
    });
  }, [enableWeekSelection, weekSelectionMinDate, weekSelectionMaxDate, fieldRole]);

  useEffect(() => {
    if (!enableWeekSelection || !isOpen) return;
    const handleWeekClick = (e) => {
      const target = e.target;
      if (!target || !target.classList.contains("MuiDayCalendar-weekNumber")) return;
      e.preventDefault();
      e.stopPropagation();
      if (target.classList.contains("week-number-disabled")) return;
      const weekText = target.textContent;
      if (!weekText) return;
      const weekNumber = parseInt(weekText, 10);
      if (isNaN(weekNumber)) return;
      const popper = target.closest(".MuiPickersPopper-root");
      if (!popper) return;
      const monthHeader = popper.querySelector(".MuiPickersCalendarHeader-label");
      if (!monthHeader) return;
      const headerText = monthHeader.textContent || "";
      const yearMatch = headerText.match(/\d{4}/);
      const displayedYear = yearMatch ? parseInt(yearMatch[0], 10) : new Date().getFullYear();
      const displayedMonth = getMonthFromHeader(headerText);

      const { monday, sunday } = getWeekBounds(weekNumber, displayedYear, displayedMonth);
      const anchorDate = fieldRole === "end" ? sunday : monday;
      const weekMoment = moment(anchorDate);
      // Reject weeks whose committed day is outside min/max (end role uses Sunday).
      if (!isDateValid(weekMoment)) return;
      isUserEditedRef.current = true;
      setSelectedDate(weekMoment);
      setValidationError("");
      onChangeCall(weekMoment.toDate());

      // Close picker and blur input using same pattern as closePicker
      isClosingRef.current = true;
      setIsOpen(false);
      window.getSelection()?.removeAllRanges();
      if (setDialogExpanded) {
        setDialogExpanded(false);
      }

      // Dual-timeout blur for reliable focus removal
      setTimeout(() => {
        inputRef.current?.blur();
      }, 150);
      setTimeout(() => {
        inputRef.current?.blur();
        isClosingRef.current = false;
      }, 600);

      onWeekSelect(monday, sunday, fieldRole);
    };
    document.addEventListener("mousedown", handleWeekClick, true);
    return () => document.removeEventListener("mousedown", handleWeekClick, true);
  }, [enableWeekSelection, isOpen, onWeekSelect, setDialogExpanded, onChangeCall, fieldRole, isDateValid]);

  useEffect(() => {
    if (!enableWeekSelection || !isOpen) return;
    const timeoutId = setTimeout(updateWeekNumberStyling, 50);
    const observer = new MutationObserver(() => updateWeekNumberStyling());
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [enableWeekSelection, isOpen, updateWeekNumberStyling]);

  const shouldDisableDate = enabledDates
    ? (date) => {
      const dateObj = new Date(date.year(), date.month(), date.date());
      return !enabledDates(dateObj);
    }
    : undefined;

  const effectiveError = disabled ? "" : (validationError || fieldError || "");
  const slotProps = getSlotProps(handleOpen, isOpen, handleInputBlur, inputRef, effectiveError, showSelection, handleKeyDown, reserveErrorSpace, highlightedDates, highlightLegend);

  return (
    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="en-gb">
      <Box width="100%">
        <DesktopDatePicker
          sx={{ mt: 0.125 }}
          label={label}
          value={selectedDate}
          onChange={handleDateChange}
          open={isOpen}
          onOpen={() => {
            if (!disabled) {
              const errorAge = errorSetAtRef.current !== null
                ? Date.now() - errorSetAtRef.current
                : Infinity;
              if (errorAge > ERROR_IMMUNITY_MS) {
                setValidationError("");
              }
              setIsOpen(true);
              if (setDialogExpanded) {
                setDialogExpanded(true);
              }
            }
          }}
          onClose={() => closePicker(true)}
          view={pickerView}
          onViewChange={(newView) => {
            setPickerView(newView);
            preventScrollOnViewChange();
          }}
          format="DD/MM/YYYY"
          disabled={disabled}
          minDate={minSelectable ? moment(minSelectable) : undefined}
          maxDate={effectiveMaxDate}
          shouldDisableDate={shouldDisableDate}
          displayWeekNumber
          showDaysOutsideCurrentMonth
          views={["year", "month", "day"]}
          openTo="day"
          slots={{
            textField: StyledDateTextField,
            ...(highlightLegend ? { actionBar: HighlightLegendActionBar } : {}),
          }}
          slotProps={slotProps}
        />
      </Box>
    </LocalizationProvider>
  );
}
